// Mixin para hacer que las tablas tengan headers sticky
export const StickyTableMixin = {
  mounted() {
    // Aplicar sticky headers a todas las tablas cuando el componente se monta
    this.$nextTick(() => {
      this.applyStickyHeaders();
    });
  },
  
  updated() {
    // Volver a aplicar cuando el componente se actualiza (por ejemplo, cuando cambian los datos)
    this.$nextTick(() => {
      this.applyStickyHeaders();
    });
  },
  
  methods: {
    applyStickyHeaders() {
      // Buscar todas las tablas en este componente
      const tables = this.$el.querySelectorAll('table.table');
      
      tables.forEach(table => {
        // Solo aplicar si la tabla tiene contenido significativo
        const tbody = table.querySelector('tbody');
        const rows = tbody ? tbody.querySelectorAll('tr') : [];
        
        // Ignorar tablas con clase no-sticky-override
        if (table.classList.contains('no-sticky-override')) {
          return;
        }
        
        // Solo aplicar sticky headers si hay más de 10 filas (listados largos)
        // O si el contenedor tiene la clase 'always-sticky-table'
        const shouldBeSticky = rows.length > 10 || 
                              table.closest('.always-sticky-table') !== null ||
                              this.$el.classList.contains('auto-sticky-tables');
        
        if (shouldBeSticky) {
          // Agregar clase wrapper si no existe
          if (!table.closest('.table-sticky-header')) {
            this.wrapTableForSticky(table);
          }
        } else {
          // Remover wrapper si hay pocas filas y no está forzado
          this.unwrapTableFromSticky(table);
        }
      });
    },
    
    wrapTableForSticky(table) {
      // Verificar si ya está envuelta
      if (table.closest('.table-sticky-header')) {
        return;
      }
      
      // Crear wrapper
      const wrapper = document.createElement('div');
      wrapper.className = 'table-sticky-header';
      
      // Determinar altura basada en el contexto
      const height = this.calculateTableHeight(table);
      if (height) {
        wrapper.style.maxHeight = height;
      }
      
      // Insertar wrapper antes de la tabla
      table.parentNode.insertBefore(wrapper, table);
      
      // Mover tabla dentro del wrapper
      wrapper.appendChild(table);
      
      // Agregar detección de scroll para efectos visuales
      this.addScrollEffects(wrapper);
      
      // Agregar event listener para debug (opcional)
      if (process.env.NODE_ENV === 'development') {
        wrapper.addEventListener('scroll', () => {
          console.log('Sticky table scrolling:', table);
        });
      }
    },
    
    unwrapTableFromSticky(table) {
      const wrapper = table.closest('.table-sticky-header');
      if (wrapper && wrapper.parentNode) {
        // Remover event listeners
        this.removeScrollEffects(wrapper);
        
        // Mover tabla fuera del wrapper
        wrapper.parentNode.insertBefore(table, wrapper);
        
        // Remover wrapper
        wrapper.remove();
      }
    },
    
    calculateTableHeight(table) {
      // Calcular altura óptima basada en el contexto del componente
      const container = table.closest('.app-card, .content-body, .app-modal');
      
      if (container) {
        if (container.classList.contains('app-modal')) {
          return '50vh';
        } else if (container.classList.contains('app-card')) {
          return '60vh';
        }
      }
      
      return '70vh'; // Valor por defecto
    },
    
    addScrollEffects(wrapper) {
      const scrollHandler = () => {
        if (wrapper.scrollTop > 0) {
          wrapper.classList.add('scrolling');
        } else {
          wrapper.classList.remove('scrolling');
        }
      };
      
      wrapper.addEventListener('scroll', scrollHandler);
      wrapper._scrollHandler = scrollHandler; // Guardar referencia para limpieza
    },
    
    removeScrollEffects(wrapper) {
      if (wrapper._scrollHandler) {
        wrapper.removeEventListener('scroll', wrapper._scrollHandler);
        delete wrapper._scrollHandler;
      }
      wrapper.classList.remove('scrolling');
    },
    
    // Método para forzar sticky headers (para usar manualmente si es necesario)
    forceStickyHeaders() {
      const tables = this.$el.querySelectorAll('table.table');
      tables.forEach(table => {
        this.wrapTableForSticky(table);
      });
    },
    
    // Método para remover todos los sticky headers
    removeStickyHeaders() {
      const tables = this.$el.querySelectorAll('table.table');
      tables.forEach(table => {
        this.unwrapTableFromSticky(table);
      });
    },
    
    // Método para actualizar sticky headers (útil cuando cambia el tamaño de la ventana)
    updateStickyHeaders() {
      this.removeStickyHeaders();
      this.$nextTick(() => {
        this.applyStickyHeaders();
      });
    }
  },
  
  // Limpiar cuando el componente se destruye
  beforeUnmount() {
    this.removeStickyHeaders();
  },
  
  // Para Vue 2 compatibility
  beforeDestroy() {
    this.removeStickyHeaders();
  }
};

export default StickyTableMixin;
